// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import LoadingIcon from "../../components/LoadingIcon"; // imported for testing
import { fetchAssetGroupById, fetchAssetGroups } from "../../store/assetGroupSlice"; // imported for testing
import { fetchAssetsByGroupId } from "../../store/assetsSlice"; // imported for testing
import { modalViewsConst, setModalState } from "../../store/modalSlice";
import AssetMap from "./AssetMap";
import AssetDashboardCards from "./AssetDashboardCards";
import AssetDashboardHeader from "./AssetDashboardHeader";
import NewNavbar from "./NewNavbar";
import AssetDashboardTopBar from "./AssetDashboardTopBar";

// This component represents the main Asset Dashboard. It includes the header, tabs, and conditionally renders either the insights cards or the asset map based on the active tab.

const AssetDashboard = () => {
  const [activeTab, setActiveTab] = useState<"insights" | "map">("insights"); // State to track the active tab
  const { groupid } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const assets = useSelector((state) => state.assets);
  const assetGroups = useSelector((state) => state.assetGroups);
  const user = useSelector((state) => state.user);

  const groups = useMemo(() => assetGroups?.groups || {}, [assetGroups?.groups]);
  const primaryGroupId = useMemo(() => {
    const groupIds = Object.keys(groups);
    return groupIds.length > 0 ? groupIds[0] : null;
  }, [groups]);
  const activeGroupId = groupid || primaryGroupId;
  const assetGroup = activeGroupId ? groups[activeGroupId] : null;
  const assetList = activeGroupId ? assets?.dataGroups?.[activeGroupId] : null;
  const hasAssets = Array.isArray(assetList) && assetList.length > 0;
  const organization = user?.organizations?.data?.[assetGroup?.organizationId];
  const ownerOrOrgLabel = useMemo(() => {
    if (organization?.name) {
      return organization.name;
    }

    const isCurrentUserOwner = assetGroup?.owner && assetGroup.owner === user?.permissionDetails?.id;
    if (isCurrentUserOwner) {
      return user?.permissionDetails?.name || user?.details?.name || "You";
    }

    return assetGroup?.owner || "Unknown Owner";
  }, [organization?.name, assetGroup?.owner, user?.permissionDetails?.id, user?.permissionDetails?.name, user?.details?.name]);

  useEffect(() => {
    if (!Object.keys(groups).length) {
      dispatch(fetchAssetGroups());
    }
  }, [dispatch, groups]);

  // GroupPage-style backend loading behavior for selected group + its assets.
  useEffect(() => {
    if (!activeGroupId) return;

    if (!groups[activeGroupId]) {
      dispatch(fetchAssetGroupById(activeGroupId));
    }

    if (!assets?.dataGroups?.[activeGroupId]) {
      dispatch(fetchAssetsByGroupId(activeGroupId));
    }
  }, [dispatch, activeGroupId, groups, assets?.dataGroups]);

  // Redirect to the first available group when route has no group id.
  useEffect(() => {
    if (!groupid && primaryGroupId) {
      navigate(`/home/asset-dashboard/${primaryGroupId}`, { replace: true });
    }
  }, [groupid, primaryGroupId, navigate]);

  const handleAddAsset = async () => {
    if (!activeGroupId) return;

    const authSession = await fetchAuthSession();
    const groupsFromToken = authSession?.tokens?.idToken?.payload?.["cognito:groups"] ?? [];
    const assetCountCap = user?.permissionDetails?.assetCount ?? 3;
    let totalAssetCount = 0;

    Object.values(assets?.dataGroups || {}).forEach((groupAssetList: any) => {
      totalAssetCount += groupAssetList?.length || 0;
    });

    if (groupsFromToken?.includes("Admin") || totalAssetCount < assetCountCap) {
      dispatch(
        setModalState({
          modalOpen: true,
          modalView: modalViewsConst.addAssetNEW,
          properties: { __modalSize: "md", assetGroupId: activeGroupId },
        })
      );
      return;
    }

    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.notification,
        properties: {
          __modalSize: "sm",
          header: "Quick Note",
          details: `Assets are currently being capped at ${assetCountCap} per user. Please reach out to support@erode.ai for any inquiries`,
        },
      })
    );
  };

  // Show loading state if selected group details or assets are still loading.
  if (activeGroupId && (!assetGroup || !assetList)) {
    return (
      <div className="flex-1 flex h-screen max-h-screen max-w-full overflow-hidden">
        <div className="rounded-[8px] bg-white p-4 flex-1 flex items-center justify-center max-w-full min-h-0">
          <LoadingIcon fullPage={false} />
        </div>
      </div>
    );
  }

  return (
    // Main container for the Asset Dashboard
    <div className="flex-1 flex h-screen max-h-screen max-w-full overflow-hidden">
      <div className="rounded-[8px] p-4 flex-1 flex flex-col max-w-full min-h-0 gap-3">
        <NewNavbar 
          organization={organization} 
          groupName={assetGroup?.name}
        />
        <AssetDashboardHeader
          activeTab={activeTab}
          onTabChange={setActiveTab}
          assetGroup={assetGroup}
          assetCount={(assetList || []).length}
          onAddAsset={handleAddAsset}
          ownerOrOrgLabel={ownerOrOrgLabel}
        /> {/* Header with tabs */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollable-div gap-3 flex flex-col">
          {hasAssets ? (
            <>
              <AssetDashboardTopBar
                title={activeTab === "insights" ? "Top Insights" : "Your Asset"}
                className={activeTab === "map" ? "hidden lg:flex" : ""}
              />
              {activeTab === "insights" ? (
                <AssetDashboardCards assetItems={assetList ?? []} groupId={activeGroupId} /> /* Render insights cards if the active tab is "insights" */
              ) : (
                <AssetMap assetItems={assetList ?? []} assetGroup={assetGroup ?? null} /> /* Render asset map if the active tab is "map" */
              )}
            </>
          ) : (
            <div className="flex flex-1 min-h-[320px] items-center justify-center rounded-[8px] border border-eai-bg-lt2 bg-white">
              <div className="flex flex-col items-center gap-3 px-4 text-center">
                <p className="text-sm text-slate-600">No assets found in this group.</p>
                <button
                  type="button"
                  className="inline-flex h-[36px] items-center rounded-[8px] bg-eai-primary-accent px-4 text-[14px] font-semibold text-white shadow-[0_1px_3px_rgba(16,24,40,0.1)]"
                  onClick={handleAddAsset}
                >
                  New Asset
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetDashboard;
